import { atom } from "jotai";
import { atomWithRefresh } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi/docsapi.atom";
import { env } from "../env";

const logger = new Logger("templates.atom");

/**
 * Derive the current OS name for template filtering.
 * Returns "macos", "windows", "linux", or undefined if unknown.
 */
function getCurrentOS(): string | undefined {
	if (env.platform === "darwin") {
		return "macos";
	}
	if (env.platform === "win32" || env.isWindows) {
		return "windows";
	}
	if (env.platform === "linux") {
		return "linux";
	}

	if (typeof navigator !== "undefined") {
		const ua = navigator.userAgent;
		if (ua.includes("Mac")) {
			return "macos";
		}
		if (ua.includes("Windows")) {
			return "windows";
		}
		if (ua.includes("Linux")) {
			return "linux";
		}
	}

	return undefined;
}

export type TemplateWorkspace = {
	id: string;
	name: string;
	host: string;
	slug: string;
	role: string;
	owner: boolean;
};

export type Template = {
	id: string;
	title: string;
	description?: string;
	createdAt: string;
	updatedAt: string;
	type: string;
	labels?: string[];
	os?: string[];
	workspace: TemplateWorkspace;
};

/**
 * Atom that fetches and caches available document templates from the API.
 * Templates are repositories in the `moment-templates` workspace that are marked as templates.
 *
 * Uses atomWithRefresh to allow manual refresh when needed.
 * Passes auth token if available for authenticated access to templates.
 */
export const $templates = atomWithRefresh(async (get): Promise<Template[]> => {
	const docsApi = get($docsApi);
	const { getAccessTokenSilently } = get($accessTokenFunc);
	const token = await getAccessTokenSilently?.();
	const os = getCurrentOS();

	try {
		const templates = await docsApi.templates.list(token, os);
		logger.debug("Fetched templates", { count: templates.length, os });
		return templates;
	} catch (error) {
		logger.error("Failed to fetch templates", { error });
		return [];
	}
});
$templates.debugLabel = "templates";

/**
 * Atom to check if templates are currently being fetched.
 * This is useful for showing loading states in the UI.
 */
export const $isLoadingTemplates = atom(false);
$isLoadingTemplates.debugLabel = "isLoadingTemplates";
$isLoadingTemplates.debugPrivate = true;
